/**
 * Partners Query Hooks
 * 
 * React Query hooks for fetching partner data.
 * 
 * @module hooks/queries/usePartners
 * 
 * @example
 * // In a component:
 * const { data: partners, isLoading } = useSearchPartners('pizza');
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { partnersService, Partner } from '../../services/partners.service';
import { queryKeys } from './queryKeys';

// =============================================================================
// QUERY HOOKS
// =============================================================================

/**
 * Hook to search partners
 * 
 * Searches partners by name, ID, or address.
 * Only runs when query is at least 1 character.
 * 
 * @param query - Search query string
 * @param limit - Maximum results (default: 20)
 * @param enabled - Whether to enable the query (default: true)
 * @returns Query result with partners array
 * 
 * @example
 * const { data: partners, isLoading, isError } = useSearchPartners('pizza', 10);
 * 
 * if (isLoading) return <Skeleton />;
 * if (isError) return <Error />;
 * return <PartnersList partners={partners} />;
 */
export const useSearchPartners = (
  query: string,
  limit: number = 20,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: queryKeys.partners.search(query),
    queryFn: () => partnersService.search({ query, limit }),
    enabled: enabled && query.trim().length >= 1,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)
  });
};

/**
 * Hook to fetch all partners
 * 
 * @returns Query result with all partners
 */
export const useAllPartners = () => {
  return useQuery({
    queryKey: queryKeys.partners.lists(),
    queryFn: () => partnersService.getAll(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch a single partner by ID
 * 
 * @param id - Partner ID
 * @returns Query result with partner data
 */
export const usePartner = (id: number) => {
  return useQuery({
    queryKey: queryKeys.partners.detail(id),
    queryFn: () => partnersService.getById(id),
    enabled: !!id,
  });
};

/**
 * Hook to fetch a partner by business ID
 * 
 * @param businessId - Business ID from HAAT system
 * @returns Query result with partner data or null
 */
export const usePartnerByBusinessId = (businessId: number) => {
  return useQuery({
    queryKey: queryKeys.partners.byBusinessId(businessId),
    queryFn: () => partnersService.getByBusinessId(businessId),
    enabled: !!businessId,
  });
};

/**
 * Hook to fetch partners by franchise ID
 * 
 * @param franchiseId - Franchise ID
 * @returns Query result with partners array
 */
export const usePartnersByFranchise = (franchiseId: string) => {
  return useQuery({
    queryKey: queryKeys.partners.byFranchise(franchiseId),
    queryFn: () => partnersService.getByFranchise(franchiseId),
    enabled: !!franchiseId,
  });
};

/**
 * Hook to fetch partners by area ID
 * 
 * @param areaId - Area ID
 * @returns Query result with partners array
 */
export const usePartnersByArea = (areaId: number) => {
  return useQuery({
    queryKey: queryKeys.partners.byArea(areaId),
    queryFn: () => partnersService.getByArea(areaId),
    enabled: !!areaId,
  });
};

// =============================================================================
// MUTATION HOOKS
// =============================================================================

/**
 * Hook to sync partners from Google Sheets
 * 
 * @returns Mutation for syncing partners
 * 
 * @example
 * const { mutate: syncPartners, isPending } = useSyncPartners();
 * 
 * syncPartners();
 */
export const useSyncPartners = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => partnersService.syncFromGoogleSheet(),
    onSuccess: () => {
      // Invalidate all partners queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: queryKeys.partners.all });
    },
  });
};

// =============================================================================
// EXPORTS
// =============================================================================

export type { Partner };

